import { inject, observer } from "mobx-react";
import { AvatarFooter, Button, Layout, NavRoute, Separator, useChat } from 'randmarcomps';
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { House, PackagePlus, Shapes, List, Truck, Store, Sprout, CornerDownLeft, Wrench, Waypoints, ArrowUpRightSquare } from "lucide-react";
import AccountWarnings from "../components/util/AccountWarnings";
import { useManufacturer } from "../contexts/WithManufacturer";
import Path from "../routes/Path";
import { RootStore } from "../stores/rootStore";

interface IProps {
  store?: RootStore;
  children: React.ReactNode;
}

const BasicLayout: React.FC<IProps> = (props) => {
  const { children, store } = props;
  const { applicationId } = useManufacturer();
  const navigate = useNavigate();

  // Scroll to top on mount
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const {
    userStore,
    categoriesStore: { categories },
    returnsStore: { returns },
    manufacturerStore: { manufacturer },
  } = store!;

  const menuItems = React.useMemo((): NavRoute[] => {
    const trim = (s: string) => s.substring(1);
    return [
      {
        label: "Home",
        key: "Home",
        icon: House,
        path: trim(Path.home(applicationId)),
        showInNav: true,
      },
      {
        label: "Insert Product",
        key: "Insert Product",
        icon: PackagePlus,
        path: trim(Path.insertProduct(applicationId)),
        showInNav: !!(categories && categories.length > 0),
      },
      {
        label: "Categories",
        key: "Categories",
        icon: Shapes,
        path: trim(Path.categories(applicationId)),
        showInNav: true,
      },
      {
        label: "Draft Purchases",
        key: "Draft Purchases",
        icon: List,
        path: trim(Path.requisitions(applicationId)),
        showInNav: true,
      },
      {
        label: "Orders",
        key: "Orders",
        icon: Truck,
        path: trim(Path.orderDetails(applicationId)),
        showInNav: true,
      },
      {
        label: "Resellers",
        key: "Resellers",
        icon: Store,
        path: trim(Path.resellers(applicationId)),
        showInNav: !!(categories && categories.length > 0),
      },
      {
        label: "Opportunities",
        key: "Opportunities",
        icon: Sprout,
        path: trim(Path.opportunities(applicationId)),
        showInNav: true,
      },
      {
        label: "Returns",
        key: "Returns",
        icon: CornerDownLeft,
        path: trim(Path.returns(applicationId)),
        showInNav: !!(returns && returns.length > 0),
      },
      {
        label: "Operations",
        key: "Operations",
        icon: Waypoints,
        path: trim(Path.operations(applicationId)),
        showInNav: true,
      },
      {
        label: "Utilities",
        key: "Utilities",
        icon: Wrench,
        path: trim(Path.sitemap(applicationId)),
        showInNav: true,
      },
    ];
  }, [applicationId, categories, returns]);

  const handleSearch = (q: string) => {
    const searchParams = new URLSearchParams({ q });
    navigate({
      pathname: "/" + manufacturer?.manufacturerId + "/Search",
      search: searchParams.toString()
    });
  };

  const footer = (
    <>
      <a
        href={`https://dashboard.randmar.io/${manufacturer?.manufacturerId}`}
        className="no-underline"
        target="_blank"
        rel="noreferrer"
      >
        <Button className="w-full">
          Partner Dashboard <ArrowUpRightSquare />
        </Button>
      </a>
      <Separator className="bg-sidebar-border" />
      <AvatarFooter onClick={() => { window.open('https://dashboard.randmar.io/' + manufacturer?.manufacturerId + '/MyAccount#brand'); }} partnerId={manufacturer?.manufacturerId ?? ""} applicationName={userStore.getApplicationName()} publicName={manufacturer?.publicName || manufacturer?.name} />
    </>
  );
  const { chatRef } = useChat();

  return (
    <>
      <Layout
        appID={applicationId}
        footer={footer}
        routes={menuItems}
        dashboardTitle="Manufacturer"
        handleValidSearch={handleSearch}
        chatRoutePath={Path.chat(applicationId)}
        chatUserName={userStore.getApplicationName()}
        chatRef={chatRef}
      >
        {store && <AccountWarnings />}
        {children}
      </Layout>
    </>
  );
};

export default inject("store")(observer(BasicLayout));
